import React from 'react';
import { mapValues, isBoolean, get, keyBy, map, some, has, isNull, isArray } from 'lodash';
import { scrollErrorIntoView } from '../../app-main/components/reduxForms/utilities/scrollErrorIntoView';
import accessTypes from './accessTypes';
import { FormattedMessage } from 'react-intl';

const hasItem = (keys, faqData) => {
  return (
    some(keys, (question) => {
      const answer = faqData[question];

      if (!has(faqData, question)) return false;
      if ('' === answer) return false;
      if (isNull(answer)) return false;
      if (isArray(answer) && 0 === answer.length) return false;

      return true;
    })
  );
};

const accessTypeLabels = (answer) => {
  const keyByAccess = keyBy(accessTypes, 'key');
  return map(answer, (item) => {
    return get(keyByAccess, [item, 'label']);
  });
};

const mapBoolToString = (obj) => {
  return mapValues(obj, (value) => {
    if (isBoolean(value)) {
      if (value) return 'true';
      return 'false';
    }
    return value;
  });
};

const showFormError = (errors) => {
  const errorKey = get(Object.keys(errors), '0');

  if (!errorKey || '_error' === errorKey) {
    return;
  }

  return scrollErrorIntoView(errors);
};

const translationMessages = {
  hasNaturalLight: <FormattedMessage id="hosting.light_faq.has_natural_light" defaultMessage="Space has natural light"/>,
  noNaturalLight: <FormattedMessage id="hosting.light_faq.no_natural_light" defaultMessage="Space does not have natural light"/>,
  canBlockOutLight: <FormattedMessage id="hosting.light_faq.can_block_out_light" defaultMessage="Natural light can be blocked out"/>,
  cannotBlockOutLight: <FormattedMessage id="hosting.light_faq.cannot_block_out_light" defaultMessage="Natural light can not be blocked out"/>,
  lightingDescription: <FormattedMessage id="hosting.light_faq.lighting_description" defaultMessage="Lighting description"/>,
  hasSoundProof: <FormattedMessage id="hosting.sound_faq.has_sound_proof" defaultMessage="Space is soundproof"/>,
  noSoundProof: <FormattedMessage id="hosting.sound_faq.not_sound_proof" defaultMessage="Space is not soundproof"/>,
  ambientNoiseDescription: <FormattedMessage id="hosting.sound_faq.ambient_noise_description" defaultMessage="Ambient noise description"/>,
  spaceAccess: <FormattedMessage id="hosting.access_faq.space_crew_access" defaultMessage="Crew access"/>,
  equipmentLoadingDescription: <FormattedMessage id="hosting.access_faq.equipment_loading_description" defaultMessage="Equipment loading description"/>,
  hasStaging: <FormattedMessage id="hosting.access_faq.staging_area" defaultMessage="Staging area is available"/>,
  noStaging: <FormattedMessage id="hosting.access_faq.staging_area_not_available" defaultMessage="No staging area available"/>,
  hasOffLimit: <FormattedMessage id="hosting.access_faq.rooms_off_limit" defaultMessage="Some rooms are off-limits for shooting"/>,
  noOffLimit: <FormattedMessage id="hosting.access_faq.rooms_available" defaultMessage="All rooms are available for shooting"/>,
  canFilmExterior: <FormattedMessage id="hosting.access_faq.film_exterior_allowed" defaultMessage="Shooting the exterior of the building is allowed"/>,
  cannotFilmExterior: <FormattedMessage id="hosting.access_faq.film_exterior_not_allowed" defaultMessage="Shooting the exterior of the building is not allowed"/>,
  electricalSystemDescription: <FormattedMessage id="hosting.electrical_faq.electrical_system_description" defaultMessage="Electrical system description"/>,
  canAccessCircuitBreaker: <FormattedMessage id="hosting.electrical_faq.can_access_circuit_breaker" defaultMessage="Circuit breaker can be accessed"/>,
  cannotAccessCircuitBreaker: <FormattedMessage id="hosting.electrical_faq.no_access_circuit_breaker" defaultMessage="Circuit breaker can not be accessed"/>,
  generalRules: <FormattedMessage id="hosting.rules_faq.general_rules" defaultMessage="General Rules"/>,
  minAge: <FormattedMessage id="hosting.rules_faq.min_age_allowed" defaultMessage="21+ age requirement"/>,
  allAge: <FormattedMessage id="hosting.rules_faq.all_age_allowed" defaultMessage="All ages are allowed in the space"/>,
  prodInsuranceRequired: <FormattedMessage id="hosting.rules_faq.production_insurance_required" defaultMessage="Production insurance is required"/>,
  prodInsuranceNotRequired: <FormattedMessage id="hosting.rules_faq.production_insurance_optional" defaultMessage="Production insurance is not required"/>,
  canMoveFurniture: <FormattedMessage id="hosting.rules_faq.can_move_furniture" defaultMessage="Furniture and decorations may be moved"/>,
  cannotMoveFurniture: <FormattedMessage id="hosting.rules_faq.cannot_move_furniture" defaultMessage="No moving furniture and decorations"/>,
  smokeMachineAllowed: <FormattedMessage id="hosting.rules_faq.smoke_machine_allowed" defaultMessage="Smoke machines are allowed"/>,
  smokeMachineNotAllowed: <FormattedMessage id="hosting.rules_faq.smoke_machine_not_allowed" defaultMessage="No smoke machines allowed"/>,
  nudityAllowed: <FormattedMessage id="hosting.rules_faq.nudity_allowed" defaultMessage="Nudity is allowed"/>,
  nudityNotAllowed: <FormattedMessage id="hosting.rules_faq.nudity_not_allowed" defaultMessage="No nudity allowed"/>,
  lighting: <FormattedMessage id="hosting.production_questiong_labels.lighting" defaultMessage="Lighting"/>,
  sound: <FormattedMessage id="hosting.production_questiong_labels.sound" defaultMessage="Sound"/>,
  access: <FormattedMessage id="hosting.production_questiong_labels.space_access" defaultMessage="Space access"/>,
  electrical: <FormattedMessage id="hosting.production_questiong_labels.electrical" defaultMessage="Electrical"/>,
  rules: <FormattedMessage id="hosting.production_questiong_labels.host_rules" defaultMessage="Host rules"/>
};

const lightFAQ = (answer) => (
  {
    has_natural_light: {
      label: answer ? translationMessages.hasNaturalLight : translationMessages.noNaturalLight
    },
    can_block_out_light: {
      label: answer ? translationMessages.canBlockOutLight : translationMessages.cannotBlockOutLight
    },
    lighting_description: {
      label: translationMessages.lightingDescription,
      description: answer
    }
  }
);

const soundFAQ = (answer) => (
  {
    has_soundproofing: {
      label: answer ? translationMessages.hasSoundProof : translationMessages.noSoundProof
    },
    ambient_noise_description: {
      label: translationMessages.ambientNoiseDescription,
      description: answer
    }
  }
);

const accessFAQ = (answer) => (
  {
    space_access_types: {
      label: translationMessages.spaceAccess,
      description: accessTypeLabels(answer).join(', ')
    },
    equipment_loading_description: {
      label: translationMessages.equipmentLoadingDescription,
      description: answer
    },
    has_separate_crew_area: {
      label: answer ? translationMessages.hasStaging : translationMessages.noStaging
    },
    separate_crew_area_description: {
      description: answer
    },
    has_off_limit_areas: {
      label: answer ? translationMessages.hasOffLimit : translationMessages.noOffLimit
    },
    off_limit_areas_description: {
      description: answer
    },
    can_film_exterior: {
      label: answer ? translationMessages.canFilmExterior : translationMessages.cannotFilmExterior
    }
  }
);

const electricalFAQ = (answer) => (
  {
    electrical_system_description: {
      label: translationMessages.electricalSystemDescription,
      description: answer
    },
    can_access_circuit_breaker: {
      label: answer ? translationMessages.canAccessCircuitBreaker : translationMessages.cannotAccessCircuitBreaker
    }
  }
);

const rulesFAQ = (answer) => (
  {
    rules: {
      label: translationMessages.generalRules,
      description: answer
    },
    min_age: {
      label: 21 === answer ? translationMessages.minAge : translationMessages.allAge
    },
    requires_production_insurance: {
      label: answer ? translationMessages.prodInsuranceRequired : translationMessages.prodInsuranceNotRequired
    },
    insurance_requirement_description: {
      description: answer
    },
    can_move_furniture: {
      label: answer ? translationMessages.canMoveFurniture : translationMessages.cannotMoveFurniture
    },
    furniture_move_description: {
      description: answer
    },
    can_use_smoke_machines: {
      label: answer ? translationMessages.smokeMachineAllowed : translationMessages.smokeMachineNotAllowed
    },
    nudity_allowed: {
      label: answer ? translationMessages.nudityAllowed : translationMessages.nudityNotAllowed
    }
  }
);

const productionQuestions = {
  lighting: {
    label: translationMessages.lighting,
    icon: 'lighting',
    keys: [
      'has_natural_light',
      'can_block_out_light',
      'lighting_description'
    ],
    faq: lightFAQ
  },
  sound: {
    label: translationMessages.sound,
    icon: 'sound',
    keys: [
      'has_soundproofing',
      'ambient_noise_description'
    ],
    faq: soundFAQ
  },
  access: {
    label: translationMessages.access,
    icon: 'access',
    keys: [
      'space_access_types',
      'equipment_loading_description',
      'has_separate_crew_area',
      'separate_crew_area_description',
      'has_off_limit_areas',
      'off_limit_areas_description',
      'can_film_exterior'
    ],
    faq: accessFAQ
  },
  electrical: {
    label: translationMessages.electrical,
    icon: 'electrical',
    keys: [
      'can_access_circuit_breaker',
      'electrical_system_description'
    ],
    faq: electricalFAQ
  },
  rules: {
    label: translationMessages.rules,
    icon: 'rules',
    keys: [
      'requires_production_insurance',
      'insurance_requirement_description',
      'can_move_furniture',
      'furniture_move_description',
      'can_use_smoke_machines',
      'nudity_allowed'
    ],
    faq: rulesFAQ
  }
};

export { mapBoolToString, showFormError, productionQuestions, hasItem };
